<template>
  <vs-wrapper>
    <vs-heading type="subhead">Select from existing batches</vs-heading>

    <vs-text-input
      label="Search by batch number"
      :value="search"
      autofocus
      @input="
        (newSearch) => {
          search = newSearch
        }
      "
    >
      <template #append>
        <v-icon>search</v-icon>
      </template>
    </vs-text-input>

    <v-progress-circular
      v-if="isLoading"
      class="ma-auto"
      :size="64"
      :width="6"
      indeterminate
      color="primary"
    />

    <div v-else-if="(batches?.length ?? 0) > 0">
      <v-list class="batches-list">
        <v-list-item-group>
          <template v-for="(batch, i) in batches">
            <v-list-item
              v-if="batch"
              :key="i"
              :disabled="batch.hubId != null && hubIdRef != batch.hubId"
              :class="{
                'greyed-out': batch.hubId != null && hubIdRef != batch.hubId,
              }"
              @click="handleSelectBatch(batch.id)"
            >
              <v-list-item-content>
                <vs-text>{{ batch.invoiceNumber }}</vs-text>
                <vs-text type="subdued"
                  >{{ formattedBatchCustomerText(batch.clientName, batch.hubName) }}
                </vs-text>
                <vs-text type="subdued"
                  >Batch Date:
                  {{ moment.utc(batch.invoiceDate).local().format('MMM DD YYYY') }}
                </vs-text>
                <vs-text v-if="batch.paymentTerms !== BatchPaymentTerms.Custom" type="subdued"
                  >Payment Terms:
                  {{ batchPaymentTermsString(batch.paymentTerms) }}
                </vs-text>
                <vs-text v-if="batch.paymentTerms === BatchPaymentTerms.Custom" type="subdued"
                  >Due Date:
                  {{
                    batch.dueDate
                      ? moment.utc(batch.dueDate).local().format('MMM DD YYYY, HH:mm')
                      : ''
                  }}
                </vs-text>

                <created-info
                  :creator-sid="batch.createdBy ?? ''"
                  :created-time="batch.created ?? undefined"
                  text-type="subdued"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list-item-group>
      </v-list>
      <vs-pagination :value="getCurrentPage" :length="totalPages" @input="updatePage" />
    </div>

    <div v-else class="text-center">
      <vs-text v-if="!search" type="caption">No batches found</vs-text>
      <vs-text v-else type="caption"
        >No results for <em>{{ search }}</em></vs-text
      >
    </div>
  </vs-wrapper>
</template>

<script setup lang="ts">
import CreatedInfo from '@/components/common/CreatedInfo.vue'
import { useSearchInvoicesQuery } from '@/api/invoice'
import { computed, reactive, ref } from 'vue'
import { useBuildSearchBatchesQuery } from '@/components/batches/query'
import moment from 'moment'
import { BatchPaymentTerms, batchPaymentTermsString } from '@/models/batch'
import { usePagination } from '@/components/jobs/pagination'

const props = defineProps<{
  hubId?: string
}>()

const search = ref('')
const { getCurrentPage, updatePage, getItemsPerPage } = usePagination()
const hubIdRef = computed(() => props.hubId)
const query = reactive(useBuildSearchBatchesQuery(getCurrentPage, getItemsPerPage, search))
const { invoices: batches, totalPages, isLoading, reFetch } = useSearchInvoicesQuery(query)

const emit = defineEmits<{
  (e: 'select', batchId: string)
}>()

function formattedBatchCustomerText(clientName: string | null, hubName: string | null) {
  if (clientName !== null && hubName !== null) {
    return `Customer: ${clientName} > ${hubName}`
  }
  return `Customer: Not selected yet`
}

function handleSelectBatch(selectedBatchId: string) {
  emit('select', selectedBatchId)
}

const refetchBatchesList = () => {
  reFetch()
}

const updateSearch = (newSearchKey: string) => {
  search.value = newSearchKey
}

defineExpose({ refetchBatchesList, updateSearch })
</script>

<style scoped>
.batches-list {
  margin: -8px -16px;
}

.greyed-out {
  opacity: 50%;
}
</style>
